import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v2Api, type ScenarioInject, type InjectType } from '@/lib/api';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Search } from 'lucide-react';

const ORDERING_OPTIONS: { value: string; label: string }[] = [
  { value: 'order', label: 'Order (asc)' },
  { value: '-order', label: 'Order (desc)' },
  { value: 'title', label: 'Title (A–Z)' },
  { value: '-title', label: 'Title (Z–A)' },
  { value: 'severity', label: 'Severity (low→high)' },
  { value: '-severity', label: 'Severity (high→low)' },
  { value: 'points', label: 'Points (low→high)' },
  { value: '-points', label: 'Points (high→low)' },
  { value: 'created_at', label: 'Date created (oldest)' },
  { value: '-created_at', label: 'Date created (newest)' },
];

const SEVERITY_OPTIONS = ['', 'low', 'medium', 'high', 'critical'];

interface ScenarioOption {
  id: string;
  name: string;
}

interface Props {
  ttxScenarios: ScenarioOption[];
}

export function AllInjectsContent({ ttxScenarios }: Props) {
  const navigate = useNavigate();
  const [injects, setInjects] = useState<ScenarioInject[]>([]);
  const [injectTypes, setInjectTypes] = useState<InjectType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterScenarioId, setFilterScenarioId] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterInjectType, setFilterInjectType] = useState<string>('');
  const [ordering, setOrdering] = useState('order');

  const fetchInjectTypes = useCallback(async () => {
    try {
      const types = await v2Api.dashboard.injectTypes.list();
      setInjectTypes(types ?? []);
    } catch {
      setInjectTypes([]);
    }
  }, []);

  const fetchInjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof v2Api.dashboard.scenarios.injects.listAll>[0] = {
        ordering,
      };
      if (search.trim()) params.search = search.trim();
      if (filterScenarioId) params.scenario_id = filterScenarioId;
      if (filterSeverity) params.severity = filterSeverity;
      if (filterInjectType) params.inject_type = parseInt(filterInjectType, 10);
      const list = await v2Api.dashboard.scenarios.injects.listAll(params);
      setInjects(list ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load injects');
      setInjects([]);
    } finally {
      setLoading(false);
    }
  }, [search, filterScenarioId, filterSeverity, filterInjectType, ordering]);

  useEffect(() => {
    fetchInjectTypes();
  }, [fetchInjectTypes]);

  useEffect(() => {
    fetchInjects();
  }, [fetchInjects]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4 p-4 rounded-lg border bg-muted/30">
        <div className="grid gap-1.5 min-w-[200px]">
          <Label htmlFor="all-injects-search">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="all-injects-search"
              placeholder="Title or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchInjects()}
              className="pl-8"
            />
          </div>
        </div>
        <div className="grid gap-1.5 min-w-[180px]">
          <Label htmlFor="all-injects-scenario">Scenario</Label>
          <select
            id="all-injects-scenario"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterScenarioId}
            onChange={(e) => setFilterScenarioId(e.target.value)}
          >
            <option value="">All scenarios</option>
            {ttxScenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5 min-w-[120px]">
          <Label htmlFor="all-injects-severity">Severity</Label>
          <select
            id="all-injects-severity"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s || 'all'} value={s}>
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5 min-w-[160px]">
          <Label htmlFor="all-injects-type">Inject type</Label>
          <select
            id="all-injects-type"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={filterInjectType}
            onChange={(e) => setFilterInjectType(e.target.value)}
          >
            <option value="">All types</option>
            {injectTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-1.5 min-w-[180px]">
          <Label htmlFor="all-injects-order">Sort by</Label>
          <select
            id="all-injects-order"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
          >
            {ORDERING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fetchInjects()}
          >
            Apply
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const scenarioId =
                filterScenarioId || (ttxScenarios[0]?.id ?? '');
              if (!scenarioId) return;
              navigate(`/injects?scenario=${scenarioId}&add=1`);
            }}
            disabled={ttxScenarios.length === 0}
          >
            Add Inject
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center text-sm text-destructive">
          {error}
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {injects.length} inject{injects.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-3">
            {injects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No injects match the current filters.
              </p>
            ) : (
              injects.map((inject) => {
                const primaryScenarioId =
                  filterScenarioId ||
                  (inject as any).scenario_id ||
                  (Array.isArray((inject as any).ttx_config_ids) &&
                    (inject as any).ttx_config_ids[0]) ||
                  '';
                return (
                <Card key={inject.id}>
                  <CardHeader className="py-3 px-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="text-muted-foreground font-normal">#{inject.order}</span>
                          {inject.title}
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {inject.description}
                        </CardDescription>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize">
                            {inject.severity}
                          </span>
                          {inject.inject_type?.name && (
                            <span className="text-xs px-2 py-0.5 rounded bg-muted">
                              {inject.inject_type.name}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {inject.points ?? 0} pts{' '}
                            {inject.points_type && (
                              <>
                                ·{' '}
                                {inject.points_type === 'manual'
                                  ? 'Manual'
                                  : inject.points_type === 'decay'
                                  ? 'Decay'
                                  : 'Standard'}
                              </>
                            )}
                          </span>
                          {inject.scheduled_offset_minutes > 0 && (
                            <span className="text-xs text-muted-foreground">
                              +{inject.scheduled_offset_minutes} min
                            </span>
                          )}
                          {Array.isArray(inject.ttx_config_ids) &&
                            inject.ttx_config_ids.length > 1 && (
                              <span className="text-xs text-muted-foreground">
                                Linked to {inject.ttx_config_ids.length} scenarios
                              </span>
                            )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="px-2 h-7"
                          disabled={!primaryScenarioId}
                          onClick={() =>
                            primaryScenarioId &&
                            navigate(
                              `/injects?scenario=${primaryScenarioId}&edit=${inject.id}`,
                            )
                          }
                        >
                          Edit
                        </Button>
                        {primaryScenarioId ? (
                          <Link
                            to={`/injects?scenario=${primaryScenarioId}`}
                            className="text-xs text-primary hover:underline"
                          >
                            Manage in scenario →
                          </Link>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            disabled={!filterScenarioId && !ttxScenarios[0]}
                            onClick={() => {
                              const targetScenarioId =
                                filterScenarioId || ttxScenarios[0]?.id;
                              if (!targetScenarioId) return;
                              navigate(
                                `/injects?scenario=${targetScenarioId}&edit=${inject.id}`,
                              );
                            }}
                          >
                            Link to scenario & edit
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
